import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, CardBody } from "reactstrap";
import axios from "axios";
import TinyWeekSelector from "../../components/tiny/TinyWeekSelector";
import TinyLoading, { TinyBusy } from "components/tiny/TinyLoading";

import TinyPageHeader from "components/tiny/TinyPageHeader";
import TinyPageTabs from "components/tiny/TinyPageTabs";
import TinyTooltip from "components/tiny/TinyTooltip";
const API_BASE = "/api/tiny";

const COLORS = {
  cardBg: "#1e2530",
  headerBg: "#252d3a",
  border: "rgba(255,255,255,0.1)",
  borderLight: "rgba(255,255,255,0.05)",
  text: "#fff",
  textSub: "rgba(255,255,255,0.8)",
  green: "#2dce89",
  cyan: "#11cdef",
  purple: "#667eea",
  orange: "#fb6340",
};

const TABS = [
  { key: "clubs", label: "俱樂部", icon: "fa fa-building" },
  { key: "super_agents", label: "直營超代", icon: "fa fa-user-secret" },
  { key: "direct_agents", label: "直營代理", icon: "fa fa-user" },
];

const thStyle = {
  background: COLORS.headerBg,
  color: COLORS.text,
  fontWeight: 600,
  borderColor: COLORS.border,
  fontSize: "11px",
  whiteSpace: "nowrap",
  padding: "8px 8px",
  position: "sticky",
  top: 0,
  zIndex: 2,
  cursor: "pointer",
  userSelect: "none",
};

const tdStyle = {
  borderColor: COLORS.borderLight,
  color: COLORS.textSub,
  verticalAlign: "middle",
  padding: "7px 8px",
  fontSize: "12.5px",
  whiteSpace: "nowrap",
};

const totalRowStyle = {
  ...tdStyle,
  fontWeight: 700,
  color: COLORS.green,
  background: "rgba(45,206,137,0.08)",
  borderTop: `2px solid ${COLORS.green}`,
};

function formatNumber(v) {
  return Number(v || 0).toLocaleString("zh-TW", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function NumberCell({ value, style }) {
  const v = Number(value || 0);
  const color = v < 0 ? "#f5365c" : v > 0 ? COLORS.textSub : "rgba(255,255,255,0.35)";
  return (
    <td style={{ ...tdStyle, textAlign: "right", color, ...style }}>
      {formatNumber(v)}
    </td>
  );
}

// v2.49.0：P&L 交收裡有多少是聯盟調整（用票補回 / 實體獎勵扣除），在數字下方標一行小字。
// 金額本身不變（P&L 交收本來就是調整後的數字），只是讓人看得出哪些輸贏是聯盟改過的。
const ADJ_LABELS = { ticket_comp: "用票補回", physical_prize: "實體獎勵扣除", legacy_unexplained: "無法歸因差額" };
function PlCell({ row }) {
  const v = Number(row.pl_total || 0);
  const adj = row.pl_adjustment;
  const notApplied = row.pl_adjustment_not_applied;
  // 儲值制、特殊信用制、本部 / EcoBot（交收一律 0）都不交收 P&L —— 那些的調整跟這一欄無關，不標
  const settles = row.club_type === "credit" && !row.is_special_credit && !row.is_zero_club;
  const color = v < 0 ? "#f5365c" : v > 0 ? COLORS.textSub : "rgba(255,255,255,0.35)";
  const sign = (n) => (n > 0 ? "+" : "") + formatNumber(n);
  return (
    <td style={{ ...tdStyle, textAlign: "right", color }}>
      {formatNumber(v)}
      {settles && (adj || notApplied) ? (
        <div style={{ display: "block" }}><TinyTooltip content={
          <div>
            {Object.entries(row.pl_adjustment_by_kind || {}).map(([k, n]) => (
              <div key={k}>{ADJ_LABELS[k] || k}：{sign(n)}</div>
            ))}
            {notApplied ? <div>票券對帳有算但未套用（當時寄出的報表沒有）：{sign(notApplied)}</div> : null}
            <div style={{ marginTop: 4, opacity: 0.7 }}>明細見「聯盟 P&amp;L 調整」頁</div>
          </div>
        }>
          <div style={{ fontSize: 10, color: notApplied && !adj ? COLORS.orange : "rgba(255,255,255,0.45)", cursor: "help", marginTop: 1 }}>
            {adj ? `含聯盟調整 ${sign(adj)}` : `未套用 ${sign(notApplied)}`}
          </div>
        </TinyTooltip></div>
      ) : null}
    </td>
  );
}

// 分潤小計欄位高亮底色（強調它是前面三項分潤的小計）
const SUBTOTAL_BG = "rgba(17,205,239,0.10)";
const SUBTOTAL_HEADER_BG = "rgba(17,205,239,0.20)";

const CLUB_COLUMNS = [
  { key: "idx", label: "#", sortable: false },
  { key: "entity_name", label: "俱樂部名稱", sortable: true },
  { key: "entity_id", label: "ID", sortable: true },
  { key: "club_type_label", label: "類型", sortable: true },
  { key: "service_fee", label: "RG分潤", sortable: true, numeric: true },
  { key: "insurance", label: "保險分潤", sortable: true, numeric: true },
  { key: "mtt_profit", label: "MTT分潤", sortable: true, numeric: true },
  { key: "revenue_share", label: "分潤小計", sortable: true, numeric: true, highlight: true },
  { key: "pl_total", label: "P&L 交收", sortable: true, numeric: true },
  {
    key: "activity_total", label: "活動獎金", sortable: true, numeric: true,
    title: "來源：額外交收中心的『活動匯入』。僅信用制俱樂部計入。",
  },
  {
    key: "special_total", label: "固定額外交收", sub: "含純 Rebate", sortable: true, numeric: true,
    title: "來源：跨俱樂部固定交收規則（weekly_special_settlements）＋ 純 Rebate 模式自動算出的一列。這些都是規則自動產生、非人工逐筆登記。注意：這裡的純 Rebate 與右邊的 Tax/Rebate 是兩套獨立機制。",
  },
  {
    key: "custom_total", label: "客製額外交收", sub: "人工逐筆登記", sortable: true, numeric: true,
    title: "來源：額外交收中心由我方人員手動登記的罰款 / 補償 / 獎勵等逐筆記錄（club_extra_settlements）。",
  },
  {
    key: "tr_total",
    label: "Tax / Rebate",
    sortable: true,
    numeric: true,
    title: "v2.25.0 Tax & Rebate：ratio = RG 水前輸贏(扣JP) ÷ RG 總水，命中級距後算出金額。僅計 Ring Game（NLH / PLO / AOF），不含 MTT / SNG｜正數 = Rebate（我方補貼俱樂部）｜負數 = Tax（我方向俱樂部抽取）。與左邊『固定額外交收』內的純 Rebate 是兩套獨立機制，可能同時存在。",
  },
  { key: "settlement_total", label: "當週交收金額", sortable: true, numeric: true,
    title: "= 分潤小計 + P&L 交收 + 活動獎金 + 固定額外交收 + 客製額外交收 + Tax/Rebate" },
];

const AGENT_COLUMNS = [
  { key: "idx", label: "#", sortable: false },
  { key: "entity_name", label: "名稱", sortable: true },
  { key: "entity_id", label: "ID", sortable: true },
  { key: "club_name", label: "所屬俱樂部", sortable: true },
  { key: "club_type_label", label: "類型", sortable: true },
  { key: "revenue_share", label: "分潤小計", sortable: true, numeric: true },
  { key: "pl_total", label: "P&L 交收", sortable: true, numeric: true },
  { key: "activity_total", label: "活動獎金", sortable: true, numeric: true },
  { key: "custom_total", label: "客製額外交收", sortable: true, numeric: true },
  { key: "settlement_total", label: "當週交收金額", sortable: true, numeric: true },
];

function TinyWeeklySettlement() {
  const [weeks, setWeeks] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("clubs");
  const [sort, setSort] = useState({ field: "settlement_total", direction: "desc" });

  const loadWeeks = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/data/weeks`);
      if (res.data.code !== 1) throw new Error(res.data.msg || "載入週期失敗");
      const allWeeks = res.data.data.weeks || [];
      setWeeks(allWeeks);
      if (allWeeks.length > 0) setSelectedWeek(allWeeks[0]);
    } catch (e) {
      setError(e.message || "載入週期失敗");
    }
  }, []);

  const loadData = useCallback(async (week) => {
    if (!week) return;
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API_BASE}/weekly-settlement/summary`, {
        params: { week },
      });
      if (res.data.code !== 1) throw new Error(res.data.msg || "載入失敗");
      setData(res.data.data);
    } catch (e) {
      setError(e.message || "載入失敗");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadWeeks();
  }, [loadWeeks]);

  useEffect(() => {
    if (selectedWeek) loadData(selectedWeek);
  }, [selectedWeek, loadData]);

  const handleExport = useCallback(async () => {
    if (!selectedWeek) return;
    setExporting(true);
    try {
      const res = await axios.get(`${API_BASE}/weekly-settlement/export`, {
        params: { week: selectedWeek },
        responseType: "blob",
      });
      const contentDisposition = res.headers["content-disposition"] || "";
      const matched = contentDisposition.match(/filename="?([^"]+)"?/i);
      const filename = matched?.[1] || `${selectedWeek}_weekly_settlement.xlsx`;
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      setError("匯出失敗：" + (e.message || ""));
    } finally {
      setExporting(false);
    }
  }, [selectedWeek]);

  const columns = activeTab === "clubs" ? CLUB_COLUMNS : AGENT_COLUMNS;

  const rows = useMemo(() => {
    if (!data) return [];
    const raw = data[activeTab] || [];
    return raw.map((r) => ({
      ...r,
      club_type_label: r.club_type === "credit"
        ? (r.is_special_credit ? "特殊信用制" : "信用制")
        : "儲值制",
    }));
  }, [data, activeTab]);

  const sortedRows = useMemo(() => {
    if (!sort.field) return rows;
    const copy = [...rows];
    copy.sort((a, b) => {
      let va = a[sort.field];
      let vb = b[sort.field];
      if (typeof va === "string") va = va.toLowerCase();
      if (typeof vb === "string") vb = vb.toLowerCase();
      if (va < vb) return sort.direction === "asc" ? -1 : 1;
      if (va > vb) return sort.direction === "asc" ? 1 : -1;
      return 0;
    });
    return copy;
  }, [rows, sort]);

  const columnTotals = useMemo(() => {
    const result = {};
    const numericCols = columns.filter((c) => c.numeric);
    numericCols.forEach((col) => {
      result[col.key] = rows.reduce((sum, r) => sum + Number(r[col.key] || 0), 0);
    });
    return result;
  }, [rows, columns]);

  const handleSort = (field) => {
    setSort((prev) => ({
      field,
      direction: prev.field === field && prev.direction === "desc" ? "asc" : "desc",
    }));
  };

  const renderSortIcon = (col) => {
    if (!col.sortable) return null;
    if (sort.field !== col.key) {
      return <i className="fa fa-sort" style={{ marginLeft: 4, opacity: 0.3, fontSize: 10 }} />;
    }
    return (
      <i
        className={`fa fa-sort-${sort.direction === "asc" ? "asc" : "desc"}`}
        style={{ marginLeft: 4, color: COLORS.cyan, fontSize: 10 }}
      />
    );
  };

  return (
    <div className="content">
      {/* ⚠️ 標題原本寫「每週固定分潤交收總覽」，側欄寫「每週交收總覽」—— 用側欄那個。
          「已鎖定 / 即時試算」是這份資料的狀態 → badge。
          週選擇器是範圍控制 → scope。
          「匯出」是動作 → 放主表 tab 列的最右邊（見下方）。 */}
      <TinyPageHeader
        title="每週交收總覽"
        description="每週固定分潤的應交收金額，含俱樂部、超代與直代三層。"
        badges={
          data && data.source
            ? [{
                text: data.source === "snapshot" ? "已鎖定（= 已產 Excel/帳單）" : "即時試算（報表尚未產生）",
                tone: data.source === "snapshot" ? "success" : "warning",
              }]
            : []
        }
        /* ⚠️ header 裡的週期選擇器一律 `label="" + showMeta={false}` ——
              這兩個 prop 的預設值（label="週期"、showMeta=true）會在選擇器
              上下各多一行（標籤、「共 N 周期 / 第 i / N」），整個 scope 區
              就比雙週獎池與 EcoBot 高出兩行。header 的高度是鎖死的
              （--tph-scope-h），撐高等於整條 header 跟著長。 */
        scope={<TinyWeekSelector
                label=""
                options={weeks}
                value={selectedWeek}
                onChange={setSelectedWeek}
                showMeta={false}
              />}
      />

      {/* ⚠️ 這段原本在 tab 底下，但它算的是**整週三層全部**，
             所以不管切到哪個 tab 數字都不動 —— 讀起來像壞掉
             （Brad 2026-09-10：「看起來超奇怪」）。→ 移到 tab 上面，
             位置就說明了它的範圍是整週而不是當前分頁。
          ⚠️ 而且這四格其實是「俱樂部 ＋ 直營超代 ＋ 直營代理 ＝ 總交收金額」，
             四個等寬並排會讀成四個平行的數字，把加總關係藏掉了。
             → 總額做成主角放左邊，三個組成靠右並用 ＋ 串起來。
          🔑 當前 tab 的那一項會亮起來（邊框 + 底色）—— 這樣切 tab 仍然有回饋，
             又不會謊稱「這個數字只算當前分頁」。 */}
      {data && data.totals && (
        <div style={{
          display: "flex", alignItems: "center", flexWrap: "wrap", gap: 16,
          background: COLORS.cardBg, border: `1px solid ${COLORS.border}`,
          borderRadius: 10, padding: "12px 18px", marginBottom: 14,
        }}>
          {/* 主角：總交收金額 */}
          {(() => {
            const v = Number(data.totals.grand_total || 0);
            return (
              <div style={{ minWidth: 190 }}>
                <div style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", fontWeight: 600, marginBottom: 2 }}>
                  總交收金額
                </div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 26, fontWeight: 700, color: COLORS.green, lineHeight: 1.1 }}>
                    {formatNumber(data.totals.grand_total)}
                  </span>
                  {v !== 0 && (
                    <span style={{ fontSize: 11, fontWeight: 600, color: v > 0 ? COLORS.orange : COLORS.green }}>
                      {v > 0 ? "我方支出" : "我方收入"}
                    </span>
                  )}
                </div>
              </div>
            );
          })()}

          <span style={{ color: "rgba(255,255,255,0.35)", fontSize: 18, fontWeight: 300 }}>=</span>

          {/* 三個組成，用 ＋ 串起來；對應當前 tab 的那一項亮起來 */}
          <div style={{ display: "flex", alignItems: "stretch", flexWrap: "wrap", gap: 8, flex: 1, minWidth: 260 }}>
            {[
              { key: "clubs", label: "俱樂部", value: data.totals.clubs, color: COLORS.cyan },
              { key: "super_agents", label: "直營超代", value: data.totals.super_agents, color: COLORS.purple },
              { key: "direct_agents", label: "直營代理", value: data.totals.direct_agents, color: COLORS.orange },
            ].map((part, idx) => {
              const on = activeTab === part.key;
              const v = Number(part.value || 0);
              return (
                <React.Fragment key={part.key}>
                  {idx > 0 && (
                    <span style={{ alignSelf: "center", color: "rgba(255,255,255,0.3)", fontSize: 15 }}>+</span>
                  )}
                  <div style={{
                    flex: "1 1 120px", minWidth: 110,
                    borderRadius: 8, padding: "7px 12px",
                    background: on ? `${part.color}1f` : "transparent",
                    border: `1px solid ${on ? `${part.color}66` : "transparent"}`,
                    transition: "background 0.16s ease, border-color 0.16s ease",
                  }}>
                    <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", fontWeight: 600, whiteSpace: "nowrap" }}>
                      {part.label}
                      {on && <span style={{ color: part.color, marginLeft: 5 }}>●</span>}
                    </div>
                    <div style={{ fontSize: 17, fontWeight: 700, color: part.color, lineHeight: 1.25 }}>
                      {formatNumber(part.value)}
                    </div>
                    {v !== 0 && (
                      <div style={{ fontSize: 10, fontWeight: 600, color: v > 0 ? COLORS.orange : COLORS.green }}>
                        {v > 0 ? "我方支出" : "我方收入"}
                      </div>
                    )}
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}

      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", marginBottom: 12, textAlign: "right" }}>
        ※ 正數 = 我方應付對方（我方支出）｜ 負數 = 對方應付我方（我方收入）
        {activeTab === "clubs" && (
          <div style={{ marginTop: 2 }}>
            ※ Tax/Rebate 僅計 Ring Game（NLH / PLO / AOF），不含 MTT / SNG
          </div>
        )}
      </div>

      <Card style={{ background: COLORS.cardBg, border: `1px solid ${COLORS.border}`, borderRadius: 12 }}>

        <CardBody style={{ padding: 20 }}>
          {error && (
            <Alert color="danger" toggle={() => setError("")} style={{ borderRadius: 8 }}>
              {error}
            </Alert>
          )}


          {/* ⚠️ 這組 tab 原本是自刻的（active 綠底 + 綠底線），Brad 2026-09-10
                 指出公版沒套到這裡 → 換成 TinyPageTabs。
                 🔑 它的「active 有一層底色」正是 Brad 喜歡的那個效果，
                    已經收進公版（TinyPageTabs 的 NAV_BG，深色實底 #20293a）。
              ⚠️ 匯出 Excel 原本自己佔一行，改放這一列最右邊 —— 它匯出的
                 就是這張表，跟 tab 同列反而更說得通。 */}
          <TinyPageTabs
            bare
            style={{ marginBottom: 16 }}
            items={TABS.map((t) => ({
              ...t,
              count: data ? (data[t.key] || []).length : undefined,
            }))}
            active={activeTab}
            onChange={(k) => { setActiveTab(k); setSort({ field: "settlement_total", direction: "desc" }); }}
            right={
              <Button
                size="sm"
                disabled={!selectedWeek || exporting || loading}
                onClick={handleExport}
                style={{
                  background: "linear-gradient(135deg, #2dce89, #26a770)",
                  border: "none",
                  borderRadius: 8,
                  fontWeight: 700,
                  fontSize: 13,
                  padding: "6px 18px",
                  opacity: !selectedWeek || exporting || loading ? 0.5 : 1,
                }}
              >
                {exporting ? (
                  <><TinyBusy light style={{ marginRight: 6 }} />匯出中</>
                ) : (
                  <><i className="fa fa-download" style={{ marginRight: 6 }} />匯出 Excel</>
                )}
              </Button>
            }
          />

          {loading ? (
            <TinyLoading variant="block" text="載入週期資料" />
          ) : sortedRows.length === 0 && !error ? (
            <div style={{ textAlign: "center", padding: 40, color: "rgba(255,255,255,0.35)" }}>
              <i className="fa fa-inbox" style={{ fontSize: 36, marginBottom: 12, display: "block" }} />
              {selectedWeek ? "該週無交收資料" : "請選擇週期"}
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {columns.map((col) => (
                      <th
                        key={col.key}
                        title={col.title}
                        style={{
                          ...thStyle,
                          textAlign: col.numeric ? "right" : "left",
                          cursor: col.sortable ? "pointer" : "default",
                          ...(col.highlight ? { background: SUBTOTAL_HEADER_BG } : {}),
                        }}
                        onClick={() => col.sortable && handleSort(col.key)}
                      >
                        <div>
                          {col.label}
                          {renderSortIcon(col)}
                        </div>
                        {col.sub && (
                          <div style={{
                            fontSize: "9px", fontWeight: 400, opacity: 0.55,
                            marginTop: "1px", whiteSpace: "nowrap",
                          }}>
                            {col.sub}
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map((row, idx) => (
                    <tr
                      key={row.entity_id + (row.club_id || "")}
                      style={{
                        background: row.is_zero_club
                          ? "rgba(255,255,255,0.015)"
                          : idx % 2 === 0 ? "transparent" : "rgba(255,255,255,0.02)",
                        opacity: row.is_zero_club ? 0.45 : 1,
                        transition: "background 0.15s",
                      }}
                      onMouseEnter={(e) => { if (!row.is_zero_club) e.currentTarget.style.background = "rgba(45,206,137,0.06)"; }}
                      onMouseLeave={(e) => { if (!row.is_zero_club) e.currentTarget.style.background = idx % 2 === 0 ? "transparent" : "rgba(255,255,255,0.02)"; }}
                    >
                      <td style={{ ...tdStyle, textAlign: "center", width: 28, padding: "7px 4px", color: "rgba(255,255,255,0.35)" }}>
                        {idx + 1}
                      </td>
                      <td style={{ ...tdStyle, fontWeight: 600, color: COLORS.text }}>
                        <div>{row.entity_name}</div>
                        {row.is_zero_club && (
                          <div style={{ fontSize: 9, color: "rgba(255,255,255,0.3)", fontWeight: 400, marginTop: 1 }}>
                            本部/EcoBOT
                          </div>
                        )}
                      </td>
                      <td style={{ ...tdStyle, fontFamily: "monospace", fontSize: 12 }}>
                        {row.entity_id}
                      </td>
                      {activeTab !== "clubs" && (
                        <td style={tdStyle}>{row.club_name}</td>
                      )}
                      <td style={tdStyle}>
                        <span style={{
                          display: "inline-block",
                          padding: "2px 8px",
                          borderRadius: 4,
                          fontSize: 11,
                          fontWeight: 600,
                          background: row.club_type === "credit"
                            ? (row.is_special_credit ? "rgba(251,99,64,0.15)" : "rgba(45,206,137,0.15)")
                            : "rgba(102,126,234,0.15)",
                          color: row.club_type === "credit"
                            ? (row.is_special_credit ? COLORS.orange : COLORS.green)
                            : COLORS.purple,
                        }}>
                          {row.club_type_label}
                          {row.exclude_pl && " (不交收P&L)"}
                        </span>
                      </td>
                      {activeTab === "clubs" && <NumberCell value={row.service_fee} />}
                      {activeTab === "clubs" && <NumberCell value={row.insurance} />}
                      {activeTab === "clubs" && <NumberCell value={row.mtt_profit} />}
                      <NumberCell value={row.revenue_share} style={activeTab === "clubs" ? { background: SUBTOTAL_BG } : undefined} />
                      {activeTab === "clubs" ? <PlCell row={row} /> : <NumberCell value={row.pl_total} />}
                      <NumberCell value={row.activity_total} />
                      {activeTab === "clubs" && <NumberCell value={row.special_total} />}
                      <NumberCell value={row.custom_total} />
                      {activeTab === "clubs" && <NumberCell value={row.tr_total} />}
                      <NumberCell
                        value={row.settlement_total}
                        style={{ fontWeight: 700, color: Number(row.settlement_total) >= 0 ? COLORS.green : "#f5365c" }}
                      />
                    </tr>
                  ))}
                  {/* Totals row */}
                  {sortedRows.length > 0 && (
                    <tr>
                      <td style={{ ...totalRowStyle, textAlign: "center" }} colSpan={activeTab === "clubs" ? 4 : 5}>
                        合計
                      </td>
                      {columns.filter((c) => c.numeric).map((col) => (
                        <td
                          key={col.key}
                          style={{
                            ...totalRowStyle,
                            textAlign: "right",
                            ...(col.highlight ? { background: SUBTOTAL_BG } : {}),
                          }}
                        >
                          {formatNumber(columnTotals[col.key])}
                        </td>
                      ))}
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

export default TinyWeeklySettlement;
